import React from "react";
import { ScheduleGridData, WEEK_DAYS, WEEK_DAY_LABELS } from "../../types/schedule";
import { emptyGridRow } from "../../utils/scheduleGrid";
import { Input } from "../ui/input";
import { Colors } from "../../utils/utils.colors";

type Props = {
  value: ScheduleGridData;
  onChange: (data: ScheduleGridData) => void;
};

export const ScheduleGridEditor: React.FC<Props> = ({ value, onChange }) => {
  const updateRow = (index: number, patch: Partial<ScheduleGridData["rows"][0]>) => {
    const rows = value.rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange({ ...value, rows });
  };

  const updateCell = (rowIndex: number, day: typeof WEEK_DAYS[number], text: string) => {
    const rows = value.rows.map((r, i) =>
      i === rowIndex ? { ...r, days: { ...r.days, [day]: text } } : r
    );
    onChange({ ...value, rows });
  };

  return (
    <div className="schedule-grid-editor" data-testid="schedule-grid-editor">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Semaine (ex. du 25 au 30 mai 2026)</label>
          <Input
            value={value.weekLabel}
            onChange={(e) => onChange({ ...value, weekLabel: e.target.value })}
            placeholder="HORAIRE DES COURS POUR LA SEMAINE DU …"
            data-testid="grid-week-label"
          />
        </div>
        <div>
          <label style={labelStyle}>Faculté (en-tête du tableau)</label>
          <Input
            value={value.facultyName || ""}
            onChange={(e) => onChange({ ...value, facultyName: e.target.value })}
            placeholder="Faculté des Sciences et Technologies"
            data-testid="grid-faculty-name"
          />
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #dde2ea", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900, fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th style={thStyle}>Promotion</th>
              {WEEK_DAYS.map((d) => (
                <th key={d} style={thStyle}>{WEEK_DAY_LABELS[d]}</th>
              ))}
              <th style={{ ...thStyle, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {value.rows.map((row, ri) => (
              <tr key={ri}>
                <td style={tdStyle}>
                  <input
                    value={row.promotion}
                    onChange={(e) => updateRow(ri, { promotion: e.target.value })}
                    style={cellInputStyle}
                    placeholder="L1"
                  />
                </td>
                {WEEK_DAYS.map((d) => (
                  <td key={d} style={tdStyle}>
                    <textarea
                      value={row.days[d] || ""}
                      onChange={(e) => updateCell(ri, d, e.target.value)}
                      rows={2}
                      style={{ ...cellInputStyle, resize: "vertical", minHeight: 44 }}
                      placeholder="Cours…"
                    />
                  </td>
                ))}
                <td style={tdStyle}>
                  <button
                    type="button"
                    onClick={() => onChange({ ...value, rows: value.rows.filter((_, i) => i !== ri) })}
                    style={{ border: "none", background: "transparent", color: Colors.redColor, cursor: "pointer", fontSize: 18 }}
                    title="Supprimer la ligne"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        data-testid="grid-add-row"
        onClick={() => onChange({ ...value, rows: [...value.rows, emptyGridRow()] })}
        style={{
          marginTop: 10,
          padding: "8px 14px",
          border: `1px dashed ${Colors.primaryColor}`,
          background: "transparent",
          color: Colors.primaryColor,
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        + Ajouter une promotion
      </button>
      <p style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
        Utilisez **texte** pour mettre en gras (ex. **Examen Algorithmique**).
      </p>
    </div>
  );
};

const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 4 };
const thStyle: React.CSSProperties = { padding: "8px 6px", borderBottom: "1px solid #dde2ea", textAlign: "center", fontSize: 10, fontWeight: 700 };
const tdStyle: React.CSSProperties = { padding: 4, borderBottom: "1px solid #eee", verticalAlign: "top" };
const cellInputStyle: React.CSSProperties = { width: "100%", border: "1px solid #e2e8f0", borderRadius: 4, padding: "4px 6px", fontSize: 12, fontFamily: "inherit" };
